/**
 * Team dashboard showing active-member, project, bundle, and activity counts with the first eight
 * shared activities and navigation into team work. Uses supplied team workspace data rather than
 * fetching personal calendars.
 *
 * @module apps/web/src/components/teams/TeamOverview
 */

import type { TeamWorkspaceData } from '@fluxability/types';
import { Link } from 'react-router-dom';
import { Panel } from './shared';

/**
 * Team dashboard showing active-member, project, bundle, and activity counts with the first eight
 * shared activities and navigation into team work. Uses supplied team workspace data rather than
 * fetching personal calendars.
 */
export function TeamOverview({ workspace }: { /** Loaded team workspace containing the members, bundles, fields, and capabilities used by this panel. */
workspace: TeamWorkspaceData }) {
  const metrics = [
    [
      'Members',
      workspace.members?.filter(member => member.status === 'active').length || 0,
      'people coordinating',
    ],
    ['Projects', workspace.projects?.length || 0, 'shared outcomes'],
    ['Bundles', workspace.bundles?.length || 0, 'activity contexts'],
    ['Activities', workspace.activities?.length || 0, 'assigned work'],
  ];
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
        {metrics.map(([label, value, detail]) => (
          <article className="bg-card rounded-md border p-4" key={label}>
            <p className="text-muted-foreground text-sm">{label}</p>
            <p className="my-1 text-3xl font-semibold tabular-nums">{value}</p>
            <p className="text-muted-foreground text-xs">{detail}</p>
          </article>
        ))}
      </div>
      <Panel
        title="Shared activities"
        description="Current shared work, with personal calendars kept private."
      >
        {!workspace.activities?.length ? (
          <p className="text-muted-foreground text-sm">No shared activities yet.</p>
        ) : (
          <ul className="divide-y">
            {workspace.activities.slice(0, 8).map(activity => (
              <li
                key={activity.id}
                className="flex flex-wrap items-center justify-between gap-3 py-3"
              >
                <div>
                  <p className="font-medium">
                    {activity.core_fields?.title || activity.title || 'Untitled activity'}
                  </p>
                  <p className="text-muted-foreground text-xs">
                    {activity.due_date
                      ? `Due ${new Date(activity.due_date).toLocaleDateString()}`
                      : 'No due date'}
                  </p>
                </div>
                <span className="text-muted-foreground text-sm">
                  {activity.assigned_to_name || activity.assigned_to_email || 'Unassigned'}
                </span>
              </li>
            ))}
          </ul>
        )}
        <Link
          className="text-primary text-sm font-medium underline"
          to={`/teams/${encodeURIComponent(workspace.team.id)}/activity`}
        >
          Add team activity
        </Link>
      </Panel>
    </div>
  );
}
